'use client';

import React, { useState, useEffect } from 'react';
import { SearchEventResult } from 'searchEvent';
import * as styles from './TrendingEvents.css';
import Link from 'next/link';
import { createLinkToRedirect } from 'utils/createLinkToRedirect';
import { ANYWHERE_LOCATION_JSON } from 'constants/index';
import { isMobile as getIsMobile } from 'utils/misc';
import { EventCard, IconClose } from '@sympla/sympla-components';
import { getTrendingEvents } from 'services/searchEvent/getTrendingEvents';
import {
  analytics,
  analyticsEvents,
  analyticsLabels,
  transportMethods
} from 'lib/analytics';

export function TrendingCardSkeleton() {
  return (
    <div className={styles.skeletonContainer}>
      <div className={`${styles.skeletonImage} ${styles.skeletonPulse}`}></div>
      <div className={styles.skeletonContent}>
        <div className={`${styles.skeletonLine} ${styles.skeletonPulse}`}></div>
        <div className={`${styles.skeletonLine} ${styles.skeletonPulse}`}></div>
        <div
          className={`${styles.skeletonShortLine} ${styles.skeletonPulse}`}
        ></div>
      </div>
    </div>
  );
}

export default function TrendingEventsFetcher({
  hitsLimit,
  onClose,
  isNavbarCollapsed,
  hasMossColor
}: {
  hitsLimit: number;
  onClose: () => void;
  isNavbarCollapsed: boolean;
  hasMossColor: boolean;
}) {
  const [trendingEventsData, setTrendingEventsData] =
    useState<SearchEventResult | null>(null);
  const [isLoadingTrending, setIsLoadingTrending] = useState(false);

  const isMobile = getIsMobile();

  useEffect(() => {
    if (trendingEventsData !== null || isLoadingTrending) {
      return;
    }

    setIsLoadingTrending(true);

    let isMounted = true;

    getTrendingEvents(hitsLimit)
      .then((response) => {
        if (isMounted) {
          setTrendingEventsData(response);
        }
      })
      .finally(() => {
        if (isMounted) {
          setIsLoadingTrending(false);
        }
      });

    return () => {
      isMounted = false;
    };
  }, [hitsLimit]);

  const seeAllButton = () => {
    analytics.sendEvent(analyticsEvents.rail_view_more_clicked, {
      item_rail_title: analyticsLabels.trendingEvents,
      transport: transportMethods.sendBeacon
    });
    onClose();
  };

  const skeletonItems = Array.from({ length: hitsLimit });

  return (
    <div>
      <div className={styles.titleContainer}>
        <h3 className={styles.sectionTitle}>Eventos em alta</h3>
        <div className={styles.eventsHeader}>
          <Link
            href={createLinkToRedirect({
              param: `?s=`,
              routeType: 'search-bar-default-filter',
              userLocation: ANYWHERE_LOCATION_JSON
            })}
            className={`${styles.sectionContainerButtonLink} ${
              hasMossColor && styles.buttonLinkMoss
            } `}
            onClick={seeAllButton}
          >
            Ver tudo
          </Link>
          {isNavbarCollapsed && !isMobile && (
            <IconClose
              width={20}
              height={20}
              onClick={() => onClose()}
              className={`${styles.closeInput} ${
                hasMossColor ? styles.closeInputMoss : ''
              }`}
            />
          )}
        </div>
      </div>
      <div className={styles.eventsGrid}>
        {isLoadingTrending
          ? skeletonItems.map((_, index) => (
              <div key={index} className={styles.cardWrapper}>
                <TrendingCardSkeleton />
              </div>
            ))
          : trendingEventsData &&
            trendingEventsData.data.length > 0 && (
              <>
                {trendingEventsData.data.map((event, index) => (
                  <div key={event.id} className={styles.cardWrapper}>
                    <EventCard
                      id={event.id}
                      name={event.name}
                      images={event.images}
                      sectionPosition={index + 1}
                      location={{
                        name: event.location?.name,
                        city: event.location?.city,
                        state: event.location?.state
                      }}
                      organizer={event.organizer}
                      type={event.type}
                      url={event.url}
                      data
                      start_date={event.start_date}
                      start_date_formats={event.start_date_formats}
                      end_date={event.end_date}
                      end_date_formats={event.end_date_formats}
                      layoutCard={!isMobile ? 'lateral-on-mobile' : 'default'}
                    />
                  </div>
                ))}
              </>
            )}
      </div>
    </div>
  );
}
